import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, articles, testimonials, siteConfig } from '@/mocks/data';

export default function Home() {
  useEffect(() => {
    document.title = `${siteConfig.name} - 白色丝袜穿搭指南与时尚资讯平台`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', siteConfig.description);
  }, []);

  const featuredArticles = articles.slice(0, 4);
  const latestArticles = articles.slice(4);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: siteConfig.name,
            url: siteConfig.url,
            description: siteConfig.description,
            potentialAction: {
              '@type': 'SearchAction',
              target: `${siteConfig.url}/search?q={search_term_string}`,
              'query-input': 'required name=search_term_string',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: siteConfig.name,
            url: siteConfig.url,
            email: siteConfig.email,
            telephone: siteConfig.phone,
            address: {
              '@type': 'PostalAddress',
              addressLocality: '上海',
              addressCountry: 'CN',
              streetAddress: '南京西路1266号恒隆广场18楼',
            },
          }),
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Elegant%20fashion%20model%20wearing%20sheer%20white%20stockings%20and%20flowing%20cream%20dress%20standing%20in%20sunlit%20minimalist%20loft%20with%20large%20windows%2C%20soft%20natural%20lighting%2C%20high%20fashion%20editorial%20photography%2C%20dreamy%20ethereal%20atmosphere%2C%20cream%20and%20ivory%20color%20palette%2C%20elegant%20feminine%20composition&width=1800&height=900&seq=hero-home-01&orientation=landscape"
              alt="白丝时尚 - 白色丝袜穿搭指南"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/25"></div>
          <div className="relative z-10 flex flex-col items-center justify-center h-full w-full px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4 leading-tight">
              白色丝袜穿搭指南
            </h1>
            <p className="text-base md:text-xl text-white/90 max-w-2xl mb-8 leading-relaxed">
              探索白丝时尚的无限可能，从日常通勤到浪漫约会，让你的每一天都优雅动人
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-3">
              <Link
                to="/category/daily-wear"
                className="px-6 py-3 bg-primary-500 text-white rounded-md font-medium text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
              >
                探索穿搭灵感
              </Link>
              <Link
                to="/category/product-review"
                className="px-6 py-3 bg-white/20 backdrop-blur-sm text-white border border-white/30 rounded-md font-medium text-sm hover:bg-white/30 transition-colors duration-200 whitespace-nowrap"
              >
                查看产品评测
              </Link>
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="py-12 md:py-16 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-3">
                探索分类
              </h2>
              <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
                无论你是寻找日常穿搭灵感，还是关注最新潮流趋势，这里都能满足你的需求
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group relative overflow-hidden rounded-lg aspect-[4/3]"
                >
                  <img
                    src={cat.imageUrl}
                    alt={cat.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-white font-heading font-semibold text-lg mb-1">{cat.name}</h3>
                    <p className="text-white/80 text-xs">{cat.articleCount} 篇文章</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8 md:mb-12">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-2">
                  精选文章
                </h2>
                <p className="text-foreground-600 text-sm">编辑团队精心挑选的高质量内容</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {featuredArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-lg overflow-hidden flex flex-col sm:flex-row"
                >
                  <div className="sm:w-48 md:w-56 flex-shrink-0 overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-48 sm:h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4 md:p-5 flex flex-col justify-between flex-1">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs px-2 py-0.5 bg-accent-100 text-accent-700 rounded-full whitespace-nowrap">
                          {categories.find((c) => c.slug === article.category)?.name}
                        </span>
                        <span className="text-xs text-foreground-500">{article.readTime}</span>
                      </div>
                      <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">{article.excerpt}</p>
                    </div>
                    <div className="flex items-center gap-2 mt-3 text-xs text-foreground-500">
                      <span>{article.author}</span>
                      <span>·</span>
                      <span>{article.publishDate}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="py-12 md:py-16 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8 md:mb-12">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-2">
                  最新文章
                </h2>
                <p className="text-foreground-600 text-sm">持续更新，不错过任何时尚资讯</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {latestArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-100 rounded-lg overflow-hidden"
                >
                  <div className="overflow-hidden aspect-[16/10]">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs px-2 py-0.5 bg-accent-100 text-accent-700 rounded-full whitespace-nowrap">
                        {categories.find((c) => c.slug === article.category)?.name}
                      </span>
                      <span className="text-xs text-foreground-500">{article.readTime}</span>
                    </div>
                    <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-600 line-clamp-2 leading-relaxed">{article.excerpt}</p>
                    <div className="flex items-center gap-2 mt-3 text-xs text-foreground-500">
                      <span>{article.author}</span>
                      <span>·</span>
                      <span>{article.publishDate}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-3">
                用户口碑
              </h2>
              <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
                听听我们的读者怎么说
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {testimonials.map((t, index) => (
                <div key={index} className="bg-background-50 rounded-lg p-5 md:p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={t.avatarUrl}
                      alt={t.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-medium text-foreground-900 text-sm">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">{t.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative w-full py-16 md:py-20 px-4 md:px-6 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20fashion%20studio%20with%20white%20stockings%20displayed%20on%20mannequin%20legs%20against%20soft%20cream%20backdrop%2C%20soft%20diffused%20lighting%2C%20luxury%20boutique%20aesthetic%2C%20clean%20modern%20interior%20design&width=1800&height=600&seq=cta-home-01&orientation=landscape"
              alt="白丝时尚"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-black/35"></div>
          <div className="relative z-10 max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-white mb-4">
              加入白丝时尚社区
            </h2>
            <p className="text-white/80 text-sm md:text-base mb-6 leading-relaxed">
              订阅我们的资讯，第一时间获取最新穿搭指南、产品评测和独家优惠信息
            </p>
            <Link
              to="/about"
              className="inline-flex px-6 py-3 bg-primary-500 text-white rounded-md font-medium text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
            >
              了解更多
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}